import type { Feature, FeatureCollection, Point, Polygon } from "geojson";

import { detectionOutlineRings, MetricPoint } from "./detectionOutline";

/**
 * Grouping of realtime hotspot detections into fires, for "create a polygon
 * ignition from this cluster".
 *
 * One parameter, delta: detections within 2·delta belong to the same fire. The
 * outline is a thin sleeve around those detections and the links between them
 * (see ./detectionOutline), so it follows a curved or forked front instead of
 * spanning it the way a hull does — and, unlike a plain delta-dilation, it
 * does not stand a delta-wide envelope off the detections.
 *
 * Everything runs on the FeatureCollection already in the browser (no extra
 * request, no turf dependency). Hotspots only: a lightning strike is a point
 * ignition, never an area.
 */

/** Disk radius. Detections merge into one fire when within 2·delta. */
export const DEFAULT_DELTA_M = 500;
export const MIN_DELTA_M = 150;
export const MAX_DELTA_M = 3000;
/**
 * Fewest detections that justify an area. One or two hot pixels say a fire is
 * *somewhere near here*, not what shape it is, so they are offered as point
 * ignitions (optionally as a grid over the uncertainty area) instead.
 */
export const MIN_CLUSTER_SIZE = 3;
/**
 * Detections closer together than this are the same pixel seen more than
 * once. Geostationary sensors (HIMAWARI/AHI) rescan every ~10 minutes, so a
 * 24-hour window stacks dozens of observations on identical coordinates —
 * without collapsing them a cluster reports 31 "detections" that are two
 * places. Well under a VIIRS pixel (375 m), so genuinely adjacent pixels stay
 * distinct.
 */
export const DEDUPE_TOLERANCE_M = 25;

const M_PER_DEG_LAT = 111_320;

const lonScale = (lat: number) =>
  M_PER_DEG_LAT * Math.max(0.05, Math.cos((lat * Math.PI) / 180));

/**
 * Collapse repeat observations of the same pixel to one feature, keeping the
 * most recent (so age styling and labels stay current) and recording how many
 * observations collapsed into it as `observation_count`.
 *
 * The server already dedupes some kinds by exact location and sends its own
 * `observation_count`, so counts are *summed* rather than incremented: this
 * pass then merges only what the server could not (near-coincident pixels
 * within the tolerance) without discarding the server's tally.
 */
export const dedupeDetections = (
  collection: FeatureCollection | null | undefined,
  toleranceM: number = DEDUPE_TOLERANCE_M
): FeatureCollection => {
  const features = ((collection?.features ?? []) as Feature[]).filter(
    (f): f is Feature<Point> => f.geometry?.type === "Point"
  );
  if (!features.length) return { type: "FeatureCollection", features: [] };

  const meanLat =
    features.reduce((sum, f) => sum + f.geometry.coordinates[1], 0) /
    features.length;
  const cellLat = toleranceM / M_PER_DEG_LAT;
  const cellLon = toleranceM / lonScale(meanLat);

  const kept = new Map<string, { feature: Feature<Point>; count: number }>();
  for (const feature of features) {
    const [lon, lat] = feature.geometry.coordinates;
    const key = `${Math.round(lon / cellLon)}:${Math.round(lat / cellLat)}`;
    const incomingCount = Number(feature.properties?.observation_count) || 1;
    const existing = kept.get(key);
    if (!existing) {
      kept.set(key, { feature, count: incomingCount });
      continue;
    }
    existing.count += incomingCount;
    const incoming = Number(feature.properties?.observed_at_epoch) || 0;
    const current = Number(existing.feature.properties?.observed_at_epoch) || 0;
    if (incoming > current) existing.feature = feature;
  }

  return {
    type: "FeatureCollection",
    features: [...kept.values()].map(({ feature, count }) => ({
      ...feature,
      properties: { ...(feature.properties ?? {}), observation_count: count },
    })),
  };
};

export interface DetectionCluster {
  id: string;
  features: Feature<Point>[];
  count: number;
  /** Union of the detections' disks. */
  hull: Polygon;
  /** Newest observation epoch (ms) in the cluster — drives styling/labels. */
  newestEpoch: number | null;
  centroid: [number, number];
}

/**
 * Point-in-geometry for a project boundary: handles Polygon and MultiPolygon,
 * and subtracts interior rings (holes).
 */
export const pointInGeometry = (
  point: [number, number],
  geometry: { type?: string; coordinates?: any } | null | undefined
): boolean => {
  if (!geometry?.type || !geometry.coordinates) return false;
  const polygons: any[] =
    geometry.type === "Polygon"
      ? [geometry.coordinates]
      : geometry.type === "MultiPolygon"
        ? geometry.coordinates
        : [];
  return polygons.some((rings: [number, number][][]) => {
    if (!rings?.length || !pointInRing(point, rings[0])) return false;
    return !rings.slice(1).some((hole) => pointInRing(point, hole));
  });
};

const pointInRing = (
  point: [number, number],
  ring: [number, number][]
): boolean => {
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const [xi, yi] = ring[i];
    const [xj, yj] = ring[j];
    if (
      yi > point[1] !== yj > point[1] &&
      point[0] < ((xj - xi) * (point[1] - yi)) / (yj - yi) + xi
    ) {
      inside = !inside;
    }
  }
  return inside;
};

/**
 * Outlines of a group of detections. Usually one polygon, but detections too
 * far apart to link come back as separate ones — each with the detections it
 * contains, treated as distinct fires. Every polygon is a single closed ring:
 * the simulator fills a polygon's exterior ring and would silently ignite
 * nothing for a MultiPolygon.
 */
export const detectionUnionPolygons = (
  coords: [number, number][],
  delta: number = DEFAULT_DELTA_M
): { polygon: Polygon; memberIndices: number[] }[] => {
  if (!coords.length) return [];

  const refLon = coords[0][0];
  const refLat = coords[0][1];
  const scale = lonScale(refLat);

  const metric: MetricPoint[] = coords.map(([lon, lat]) => ({
    x: (lon - refLon) * scale,
    y: (lat - refLat) * M_PER_DEG_LAT,
  }));

  const rings = detectionOutlineRings(metric, delta);
  const out: { polygon: Polygon; memberIndices: number[] }[] = [];

  for (const ring of rings) {
    if (ring.length < 3) continue;
    const lngLat = ring.map(
      (p) => [refLon + p.x / scale, refLat + p.y / M_PER_DEG_LAT] as [number, number]
    );
    const first = lngLat[0];
    const last = lngLat[lngLat.length - 1];
    if (first[0] !== last[0] || first[1] !== last[1]) lngLat.push(first);

    const memberIndices: number[] = [];
    coords.forEach((coord, index) => {
      if (pointInRing(coord, lngLat)) memberIndices.push(index);
    });
    if (!memberIndices.length) continue;

    out.push({
      polygon: { type: "Polygon", coordinates: [lngLat] },
      memberIndices,
    });
  }

  return out;
};

/**
 * Group detections into fires and outline each one.
 *
 * Grouping is single-linkage at 2·delta — exactly the condition for two disks
 * of radius delta to overlap — evaluated over a uniform grid so only
 * neighbouring cells are compared (a pairwise scan is far too slow at the 20k
 * serving cap).
 */
export const clusterDetections = (
  collection: FeatureCollection | null | undefined,
  delta: number = DEFAULT_DELTA_M
): DetectionCluster[] => {
  const points = ((collection?.features ?? []) as Feature[]).filter(
    (f): f is Feature<Point> => f.geometry?.type === "Point"
  );
  if (points.length < MIN_CLUSTER_SIZE) return [];

  const linkDistance = delta * 2;
  const meanLat =
    points.reduce((sum, f) => sum + f.geometry.coordinates[1], 0) / points.length;
  const cellLat = linkDistance / M_PER_DEG_LAT;
  const cellLon = linkDistance / lonScale(meanLat);

  const grid = new Map<string, number[]>();
  points.forEach((f, index) => {
    const [lon, lat] = f.geometry.coordinates;
    const k = `${Math.floor(lon / cellLon)}:${Math.floor(lat / cellLat)}`;
    const bucket = grid.get(k);
    if (bucket) bucket.push(index);
    else grid.set(k, [index]);
  });

  const overlaps = (a: Feature<Point>, b: Feature<Point>) => {
    const [alon, alat] = a.geometry.coordinates;
    const [blon, blat] = b.geometry.coordinates;
    const dy = (alat - blat) * M_PER_DEG_LAT;
    const dx = (alon - blon) * lonScale((alat + blat) / 2);
    return dx * dx + dy * dy <= linkDistance * linkDistance;
  };

  const seen = new Array(points.length).fill(false);
  const clusters: DetectionCluster[] = [];

  for (let start = 0; start < points.length; start++) {
    if (seen[start]) continue;
    seen[start] = true;
    const members = [start];

    for (let cursor = 0; cursor < members.length; cursor++) {
      const current = points[members[cursor]];
      const [lon, lat] = current.geometry.coordinates;
      const cx = Math.floor(lon / cellLon);
      const cy = Math.floor(lat / cellLat);
      for (let ox = -1; ox <= 1; ox++) {
        for (let oy = -1; oy <= 1; oy++) {
          const bucket = grid.get(`${cx + ox}:${cy + oy}`);
          if (!bucket) continue;
          for (const candidate of bucket) {
            if (seen[candidate]) continue;
            if (!overlaps(current, points[candidate])) continue;
            seen[candidate] = true;
            members.push(candidate);
          }
        }
      }
    }

    if (members.length < MIN_CLUSTER_SIZE) continue;

    const groupFeatures = members.map((i) => points[i]);
    const groupCoords = groupFeatures.map(
      (f) => f.geometry.coordinates as [number, number]
    );

    for (const blob of detectionUnionPolygons(groupCoords, delta)) {
      const features = blob.memberIndices.map((i) => groupFeatures[i]);
      if (features.length < MIN_CLUSTER_SIZE) continue;
      const coords = blob.memberIndices.map((i) => groupCoords[i]);

      const epochs = features
        .map((f) => Number(f.properties?.observed_at_epoch))
        .filter((v) => Number.isFinite(v));

      clusters.push({
        id: `cluster-${features[0].id ?? members[0]}-${features.length}-${clusters.length}`,
        features,
        count: features.length,
        hull: blob.polygon,
        newestEpoch: epochs.length ? Math.max(...epochs) : null,
        centroid: [
          coords.reduce((s, c) => s + c[0], 0) / coords.length,
          coords.reduce((s, c) => s + c[1], 0) / coords.length,
        ],
      });
    }
  }

  return clusters.sort((a, b) => b.count - a.count);
};

/**
 * Cluster outlines as map-ready features (one polygon per fire).
 *
 * *isCreatable* marks fires that can actually become an ignition — the ones
 * reaching into the project area. Fires outside it are still drawn (they are
 * useful context: detections are collected from a buffer well beyond the
 * project) but styled and handled differently.
 */
export const clustersToFeatureCollection = (
  clusters: DetectionCluster[],
  isCreatable: (cluster: DetectionCluster) => boolean = () => true,
  /** Incident numbers the cluster belongs to (see utils/clusterIncidents),
   *  rendered as a label over the hull. */
  incidentIds: (cluster: DetectionCluster) => number[] | undefined = () => undefined
): FeatureCollection => ({
  type: "FeatureCollection",
  features: clusters.map((cluster) => {
    const ids = incidentIds(cluster);
    const incidentLabel =
      ids && ids.length ? ids.map((id) => `#${id}`).join(" · ") : undefined;
    return {
      type: "Feature" as const,
      id: cluster.id,
      geometry: cluster.hull,
      properties: {
        cluster_id: cluster.id,
        detection_count: cluster.count,
        newest_epoch: cluster.newestEpoch,
        creatable: isCreatable(cluster),
        ...(incidentLabel
          ? { incident_ids: ids, incident_label: incidentLabel }
          : {}),
        hoverLabel:
          `Fire cluster · ${cluster.count} detections` +
          (incidentLabel ? ` · incident ${incidentLabel}` : ""),
      },
    };
  }),
});
